// src/contexts/AuthContext.tsx

import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User, AuthChangeEvent, SupabaseClient } from '@supabase/supabase-js';
import { createClient } from './../utils/supabase-client';
import { DBTables } from '../enums/Tables';

interface AuthContextType {
    supabase: SupabaseClient<any, "public", any>,
    session: Session | null;
    user: User | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<{ error: any | null }>;
    signOut: () => Promise<void>;
    refreshSession: () => Promise<void>;
    profileData: ProfileFormData | null;
    updateProfileData: (updatedProfileData: ProfileFormData) => void;
}

interface ProfileFormData {
    role: string;
    status: string;
    profile_code: string;
    employee_type: string;
    name: string;
    age: number;
    gender: string;
    education: string;
    college: string;
    university: string;
    operational_city: string;
    created_at?: string;
    updated_at?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const supabase = createClient();

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [profileData, setProfileData] = useState<ProfileFormData | null>(null);

    // console.log('user = ', user, 'session = ', session, ' profileData=', profileData);
    // role : "no_role"

    useEffect(() => {
        // Get initial session
        const initializeAuth = async () => {
            setLoading(true);

            // Check for existing session
            const { data: { session } } = await supabase.auth.getSession();
            setSession(session);
            setUser(session?.user ?? null);

            if (!session) {
                setLoading(false);
                return;
            }

            // Set up auth listener
            const { data: { subscription } } = supabase.auth.onAuthStateChange(
                (_event: AuthChangeEvent, session: Session | null) => {
                    setSession(session);
                    setUser(session?.user ?? null);
                }
            );

            const query = supabase
                .from(DBTables.Profile)
                .select('*')
                .eq('user_id', session?.user?.id)
                // .eq('chat_id', from)
                .limit(1)
            const { data, error } = await query;
            if (error) {
                console.error("Error fetching profile data:", error);
                // return;
            } else {
                setProfileData(data?.[0] || []);
            }
            console.log('fetched for first time after login user =', session?.user ?? null, ' profile data=', data?.[0] || [])
            setLoading(false);
            // Clean up subscription
            return () => {
                subscription.unsubscribe();
            };
        };

        initializeAuth();
    }, []);

    const updateProfileData = (updatedProfileData: ProfileFormData): void => setProfileData(updatedProfileData);

    const signIn = async (email: string, password: string) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (data.session) {
            setSession(data.session);
            setUser(data.session.user);
        }

        return { error };
    };

    const signOut = async () => {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
    };

    const refreshSession = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
        setUser(session?.user ?? null);
    };

    const value = {
        supabase,
        session,
        user,
        loading,
        signIn,
        signOut,
        refreshSession,
        profileData,
        updateProfileData
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthProvider;